import {
  FeatureAccessSource,
  UnlockableFeature,
  useGetMyFeatureAccessQuery
} from '../features/subscription/graphql/featureAccess'

export type FeatureAccess = {
  /** Whether the company may use the feature right now. */
  hasAccess: boolean
  /** Why access is (or is not) granted. */
  source: FeatureAccessSource | null
  /** When a time-limited grant ends (coupon expiry, or the manual-plan cutoff). */
  accessEndsAt: Date | null
  /** Whether to run the manual-plan countdown. False for coupon holders. */
  showCountdown: boolean
  /** Whether to prompt this account to move onto an automated (Stripe) plan. */
  requiresMigration: boolean
  /** The manual-plan analytics cutoff. */
  migrationDeadline: Date | null
  /** Whether the account is on a manual (admin-granted) plan. */
  isManual: boolean
  /**
   * True while a request is in flight — including background revalidation, when
   * a previous verdict is still available. Prefer `resolved` for render
   * decisions; using `loading` there makes the UI flicker on every refetch.
   */
  loading: boolean
  /** Whether a verdict has been received at least once. */
  resolved: boolean
  /** Re-fetch the verdict (after redeeming a coupon or migrating plan). */
  refetch: () => void
}

const toDate = (value?: string | null): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * The server's entitlement verdict for a gated feature.
 *
 * The rules (Starter excluded, manual plans excluded from the cutoff, coupon
 * unlocks honoured) live server-side in `utils/featureEntitlements`, where the
 * enforcing policy also reads them. This hook deliberately re-derives nothing:
 * a second copy of the rules in the client is exactly how a UI ends up
 * promising access the API refuses.
 */
const useFeatureAccess = (feature: UnlockableFeature): FeatureAccess => {
  const { data, loading, error, refetch } = useGetMyFeatureAccessQuery({
    variables: { feature },
    // Entitlement lapses on a deadline, so always confirm against the network
    // rather than serving a stale "unlocked" verdict from cache.
    fetchPolicy: 'cache-and-network',
    // Surface errors through `error` rather than throwing; a failed entitlement
    // check must degrade to "no access", not crash the page.
    errorPolicy: 'all'
  })

  const access = data?.getMyFeatureAccess

  return {
    // Defaults are deliberately fail-closed: before a verdict arrives we treat
    // the account as unentitled, so gated content is never briefly exposed.
    hasAccess: Boolean(access?.hasAccess),
    // An error counts as resolved — fail-closed, but *decided*. Without this a
    // failing query would leave consumers that wait on `resolved` (the analytics
    // route guard) spinning forever instead of redirecting.
    resolved: data !== undefined || Boolean(error),
    source: (access?.source as FeatureAccessSource) ?? null,
    accessEndsAt: toDate(access?.accessEndsAt),
    showCountdown: Boolean(access?.showCountdown),
    requiresMigration: Boolean(access?.requiresMigration),
    migrationDeadline: toDate(access?.migrationDeadline),
    isManual: Boolean(access?.isManual),
    loading,
    refetch: () => {
      refetch()
    }
  }
}

export default useFeatureAccess
